/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new QuantitySelectorUpdateEvent(quantity, cartLine));
 * document.addEventListener(ThemeEvents.quantitySelectorUpdate, (e) => { console.log(e.detail.quantity) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlaying = 'media:started-playing';
  // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  static megaMenuHover = 'megaMenu:hover';
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  static zoomMediaSelected = 'zoom-media:selected';
  /** @static @constant {string} Event triggered when a zoom dialog media is open */
  static zoomMediaIndex = 'zoom-media:index';
  /** @static @constant {string} Event triggered when a user-visible error is shown (forms, etc.) */
  static textMessage = 'theme:text_message';
  /** @static @constant {string} Event triggered when form field validation fails */
  static validationMessage = 'theme:validation_message';
  /** @static @constant {string} Event triggered when a newsletter subscription succeeds */
  static newsletterSubscription = 'theme:newsletter_subscription';
  /** @static @constant {string} Event triggered when the PLP sort option is changed */
  static sortUse = 'theme:sort_use';
  /** @static @constant {string} Event triggered when a PLP filter value is checked on */
  static filterUse = 'theme:filter_use';
  /** @static @constant {string} Event triggered when a predictive search result is clicked */
  static searchUse = 'theme:search_use';
  /** @static @constant {string} Event triggered when a navigation menu link is clicked */
  static navigationClick = 'navigation:click';
  /** @static @constant {string} Event triggered when a paginated list appends a new page of items */
  static resultsAppended = 'theme:results_appended';
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 * @property {Event | null} [event]
 */

export class SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }

  /** @type {SlideshowSelectEventData} */
  detail;

  static eventName = 'slideshow:select';
}

/**
 * Event class for zoom dialog media selection
 * @extends {Event}
 */
export class ZoomMediaSelectedEvent extends Event {
  /**
   * Creates a new ZoomMediaSelectedEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaSelected, { bubbles: true });
    this.detail = {
      index,
    };
  }
}

/**
 * Event class for zoom dialog media index (opening the dialog directly at a given media index)
 * @extends {Event}
 */
export class ZoomMediaIndexEvent extends Event {
  /**
   * Creates a new ZoomMediaIndexEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaIndex, { bubbles: true });
    this.detail = {
      index,
    };
  }
}

/**
 * Event fired when an error message is shown to the user (non-cart forms) — GTM `text_message`.
 * @extends {Event}
 */
export class TextMessageEvent extends Event {
  /**
   * @param {string} message - User-visible error text for GTM `message_text`
   */
  constructor(message) {
    super(ThemeEvents.textMessage, { bubbles: true });
    this.detail = { message };
  }
}

/**
 * Event fired when form validation fails (submit or field validation) — GTM `validation_message`.
 * @extends {Event}
 */
export class ValidationMessageEvent extends Event {
  /**
   * @param {string} validationText - User-visible validation message
   * @param {string} validationField - Field name or id
   */
  constructor(validationText, validationField) {
    super(ThemeEvents.validationMessage, { bubbles: true });
    this.detail = {
      validation_text: validationText,
      validation_field: validationField,
    };
  }
}

/**
 * Event fired when a newsletter subscription succeeds — GTM `newsletter_subscription`.
 * @extends {Event}
 */
export class NewsletterSubscriptionEvent extends Event {
  /**
   * @param {string} formId
   * @param {string} email
   */
  constructor(formId, email) {
    super(ThemeEvents.newsletterSubscription, { bubbles: true });
    this.detail = { form_id: formId, email };
  }
}

/**
 * Event fired when the PLP sort option is changed — GTM `sort_use`.
 * @extends {Event}
 */
export class SortUseEvent extends Event {
  /**
   * @param {string} sortOption - The sort option (e.g. Shopify's `sort_by` slug, reshaped into
   *   the Measurement Plan doc's `<key> <direction>` format)
   */
  constructor(sortOption) {
    super(ThemeEvents.sortUse, { bubbles: true });
    this.detail = { sort_option: sortOption };
  }
}

/**
 * Event fired when a PLP filter value is checked on — GTM `filter_use`.
 * @extends {Event}
 */
export class FilterUseEvent extends Event {
  /**
   * @param {string} filterGroup - The filter group, e.g. `availability`, `color`, `price`
   * @param {string} filterOption - The checked input's value/label, or the price summary text
   */
  constructor(filterGroup, filterOption) {
    super(ThemeEvents.filterUse, { bubbles: true });
    this.detail = { filter_group: filterGroup, filter_option: filterOption };
  }
}

/**
 * Event fired when a predictive search result is clicked (product, collection, or suggestion) —
 * GTM `search_use`.
 * @extends {Event}
 */
export class SearchUseEvent extends Event {
  /**
   * @param {'pdp' | 'category' | 'suggestion'} searchAction
   * @param {string} searchQuery - The current search input value
   */
  constructor(searchAction, searchQuery) {
    super(ThemeEvents.searchUse, { bubbles: true });
    this.detail = { search_action: searchAction, search_query: searchQuery };
  }
}

/**
 * Event fired when a navigation menu link is clicked — GTM `navigation_use`.
 * @extends {Event}
 */
export class NavigationClickEvent extends Event {
  /**
   * @param {string[]} path - Labels from the top-level category down to the clicked link's own
   *   text, in order
   */
  constructor(path) {
    super(ThemeEvents.navigationClick, { bubbles: true });
    this.detail = {
      navigation_text: path.join(' | '),
      navigation_level: path.length,
    };
  }
}

/**
 * Event fired when a paginated list (`assets/paginated-list.js`) appends a new page of cards —
 * feeds `view_item_list`/`select_item` binding for that batch.
 * @extends {Event}
 */
export class ResultsAppendedEvent extends Event {
  /** @param {Element[] | NodeListOf<Element>} elements - The newly appended card elements */
  constructor(elements) {
    super(ThemeEvents.resultsAppended, { bubbles: true });
    this.detail = { elements: [...elements] };
  }
}

/**
 * Event class for mega menu hover being hovered over
 * @extends {Event}
 */
export class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}
